export type ServerErrorCode =
  | 'Unknown'
  | 'InvalidRequest'
  | 'GameOver'
  | 'NotYourTurn'
  | 'AlreadyFinished'
  | 'CannotSwapHidden'
  | 'SwapPhaseOver'
  | 'MustPlayIfPossible'
  | 'NoCardsSelected'
  | 'OneHiddenCardAtATime'
  | 'HiddenCardsLocked'
  | 'MixedRanks'
  | 'CardsNotOwned'
  | 'HandCardsFirst'
  | 'MixedSourcesNeedMatchingHand'
  | 'MixedSourcesWhileDeckRemains'
  | 'HandChanged'
  | 'GameNotFound'
  | 'GameFull'
  | 'NotInGame'
  | 'BadReconnectToken'
  | 'NoBotsToReplace'
  | 'AlreadyInGame'
  | 'ServerBusy'
  | 'InvalidGameSetup'
  | 'InvalidPlayerName'
  | 'LobbyNotFound'
  | 'InvalidLobbyName'
  | 'NotLobbyAdmin'
  | 'NotInLobby';

export interface IServerError {
  code: ServerErrorCode;
  message: string;
  playerIndex?: number;
}

export interface IAppError extends IServerError {
  id: string;
  occurrence: number;
}

export type ErrorTone = 'rule' | 'error';

const RULE_CODES: ReadonlySet<ServerErrorCode> = new Set<ServerErrorCode>([
  'NotYourTurn',
  'AlreadyFinished',
  'CannotSwapHidden',
  'SwapPhaseOver',
  'MustPlayIfPossible',
  'NoCardsSelected',
  'OneHiddenCardAtATime',
  'HiddenCardsLocked',
  'MixedRanks',
  'CardsNotOwned',
  'HandCardsFirst',
  'MixedSourcesNeedMatchingHand',
  'MixedSourcesWhileDeckRemains',
  'HandChanged',
]);

export const errorTone = (code: ServerErrorCode): ErrorTone =>
  RULE_CODES.has(code) ? 'rule' : 'error';

const SIGNALR_HUB_EXCEPTION_PREFIX = 'HubException: ';

function isServerError(value: unknown): value is IServerError {
  const candidate = value as Partial<IServerError> | null;
  return (
    typeof candidate?.code === 'string' && typeof candidate.message === 'string'
  );
}

function parseHubPayload(message: string): IServerError | undefined {
  const prefixIndex = message.indexOf(SIGNALR_HUB_EXCEPTION_PREFIX);
  if (prefixIndex === -1) {
    return undefined;
  }
  try {
    const payload: unknown = JSON.parse(
      message.slice(prefixIndex + SIGNALR_HUB_EXCEPTION_PREFIX.length)
    );
    return isServerError(payload) ? payload : undefined;
  } catch {
    return undefined;
  }
}

export function toServerError(e: unknown): IServerError {
  if (isServerError(e)) {
    return e;
  }
  const message = e instanceof Error ? e.message : String(e);
  return parseHubPayload(message) ?? { code: 'Unknown', message };
}

export const isSameError = (a: IServerError, b: IServerError) =>
  a.code === b.code &&
  a.message === b.message &&
  a.playerIndex === b.playerIndex;
